import {type IssueFieldSelection, parsePinnedIssueFields} from '@github-ui/issue-fields-shared/issue-field'
import {CompressedAssigneeAnchor} from '@github-ui/item-picker-assignee/CompressedAssigneeAnchor'
import {DefaultAssigneePickerAnchor} from '@github-ui/item-picker-assignee/DefaultAssigneePickerAnchor'
import type {AssigneeRepositoryPickerProps} from '@github-ui/item-picker-assignee-relay'
import {AssigneeRepositoryPicker} from '@github-ui/item-picker-assignee-relay'
import {SharedPicker} from '@github-ui/item-picker-base/SharedPicker'
import {DefaultIssueTypePickerAnchor} from '@github-ui/item-picker-issue-type/DefaultIssueTypePickerAnchor'
import {IssueTypePicker} from '@github-ui/item-picker-issue-type-tsq'
import {DefaultLabelPickerAnchor as DefaultLabelAnchor} from '@github-ui/item-picker-label/DefaultLabelPickerAnchor'
import {LabelPicker} from '@github-ui/item-picker-label-tsq'
import {DefaultMilestonePickerAnchor} from '@github-ui/item-picker-milestone/components/DefaultMilestonePickerAnchor'
import {MilestonePicker} from '@github-ui/item-picker-milestone-tsq'
import {DefaultProjectPickerAnchor} from '@github-ui/item-picker-project'
import {ProjectPicker} from '@github-ui/item-picker-project-tsq'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useAnalytics} from '@github-ui/use-analytics'
import {AlertIcon, IssueOpenedIcon, MilestoneIcon, TableIcon} from '@primer/octicons-react'
import {type RefObject, Suspense, useMemo} from 'react'

import {useIssueCreateConfigContext} from './../contexts/IssueCreateConfigContext'
import {useIssueCreateDataContext} from './../contexts/IssueCreateDataContext'
import {MetadataFooterLoading} from './../metadata/MetadataFooterLoading'
import {useIssueFieldPickers} from './hooks/use-issue-field-pickers'
import {IssueFieldValueSharedEditor, IssueFieldValueSharedPendingEditor} from './IssueFieldValueSharedEditor'
import styles from './MetadataFooter.module.css'
import type {SharedMetadataProps} from './MetadataSelectors'

export const MetadataFooter = ({
  repo,
  owner,
  canSetAssignees,
  canSetLabels,
  canSetMilestone,
  canCreateLabel,
  canCreateMilestone,
  canSetProjects,
  canSetIssueFields,
  canSetType,
  issueTypesEnabled,
  orgPinnedFields,
  fieldVisibilityMap,
}: SharedMetadataProps) => {
  const {optionConfig} = useIssueCreateConfigContext()
  const {
    repository,
    labels,
    setLabels,
    assignees,
    setAssignees,
    projects,
    setProjects,
    milestone,
    setMilestone,
    issueType,
    setIssueType,
    issueFields,
    setIssueFields,
  } = useIssueCreateDataContext()

  const showVisibilityIndicators = repository != null && !repository.isPrivate

  const sharedConfigProps = {
    insidePortal: optionConfig.insidePortal,
    anchorSide: 'outside-top' as const,
  }

  // Use issue type pinned fields if a type is selected, otherwise org pinned fields
  const mergedPinnedFields: readonly IssueFieldSelection[] = useMemo(
    () =>
      issueType
        ? (parsePinnedIssueFields(issueType.pinnedFields?.filter(f => f?.__typename !== '%other')) ?? [])
        : (orgPinnedFields ?? []),
    [issueType, orgPinnedFields],
  )

  const {
    pendingSelectedField,
    setPendingSelectedField,
    pinnedFields,
    unpinnedFields,
    onPendingFieldCommit,
    onIssueFieldValueChanged,
  } = useIssueFieldPickers({
    pinnedFieldsFromType: mergedPinnedFields,
    setIssueFieldsWithValues: setIssueFields,
    scopedFields: optionConfig.scopedFields || [],
    issueFields,
  })

  const firstSelectedProjectTitle = projects[0]?.title || ''
  const {sendAnalyticsEvent} = useAnalytics()
  const pickerProps: AssigneeRepositoryPickerProps | null =
    repository && (canSetAssignees || assignees.length > 0) && !optionConfig.scopedAssignees
      ? {
          repo: repository.name,
          owner: repository.owner.login,
          readonly: !canSetAssignees,
          includeAuthorableBots: false,
          includeAssignableBots: true,
          assignees,
          assigneeTokens: [],
          anchorElement: (
            anchorProps: React.HTMLAttributes<HTMLElement>,
            ref?: RefObject<HTMLButtonElement | null>,
          ) => (
            <DefaultAssigneePickerAnchor
              assignees={assignees}
              readonly={!canSetAssignees}
              anchorProps={{...anchorProps, id: 'assignees-select-menu'}}
              ref={ref}
              {...sharedConfigProps}
            />
          ),
          onSelectionChange: setAssignees,
          ...sharedConfigProps,
        }
      : null

  let picker: React.ReactElement | null = null
  if (pickerProps) {
    picker = <AssigneeRepositoryPicker {...pickerProps} />
  }

  return (
    <Suspense fallback={<MetadataFooterLoading />}>
      <div className={styles.MetadataFooterContainer}>
        {picker}
        {optionConfig.scopedAssignees && (
          <CompressedAssigneeAnchor
            assignees={optionConfig.scopedAssignees}
            displayHotkey={false}
            anchorProps={undefined}
            readonly
          />
        )}
        {(canSetLabels || labels.length > 0) && (
          <LabelPicker
            repo={repo}
            owner={owner}
            readonly={!canSetLabels}
            canCreateLabel={canCreateLabel}
            showNoMatchItem
            labels={labels}
            anchorElement={(anchorProps, ref) => (
              <DefaultLabelAnchor
                readonly={!canSetLabels}
                labels={labels}
                anchorProps={anchorProps}
                ref={ref}
                {...sharedConfigProps}
              />
            )}
            onSelectionChange={setLabels}
            {...sharedConfigProps}
          />
        )}
        {issueTypesEnabled && (canSetType || issueType !== null) && !optionConfig.scopedIssueType && (
          <IssueTypePicker
            owner={owner}
            repo={repo}
            onSelectionChange={issueTypes => {
              const newType = issueTypes?.[0] || null
              sendAnalyticsEvent('issue_create.update_issue_type', 'ISSUE_CREATE_UPDATE_ISSUE_TYPE_DIALOG', {
                issueTypeId: newType?.id ?? '',
              })
              setIssueType(newType)
            }}
            anchorElement={anchorProps => (
              <DefaultIssueTypePickerAnchor
                readonly={!canSetType}
                activeIssueType={issueType}
                anchorProps={anchorProps}
                {...sharedConfigProps}
              />
            )}
            readonly={!canSetType}
            activeIssueType={issueType ?? null}
            width="medium"
            {...sharedConfigProps}
          />
        )}
        {canSetIssueFields &&
          pinnedFields.map(field => (
            <IssueFieldValueSharedEditor
              key={field.field.id}
              field={field}
              onChange={onIssueFieldValueChanged}
              className={styles.animationClass}
              readonly={field.readonly}
              showVisibilityIndicator={
                showVisibilityIndicators && !!field.field.id && fieldVisibilityMap[field.field.id] === 'ALL'
              }
            />
          ))}
        {((issueTypesEnabled && (canSetType || issueType !== null)) ||
          (canSetIssueFields && pinnedFields.length > 0)) && (
          <div className={styles.MetadataFooterSeparator} data-testid="issue-fields-divider" />
        )}
        {optionConfig.scopedIssueType && (
          <SharedPicker anchorText={optionConfig.scopedIssueType} leadingIcon={IssueOpenedIcon} readonly />
        )}
        {repository && !optionConfig.scopedProjectTitle && (canSetProjects || projects.length > 0) && (
          <ErrorBoundary
            fallback={<SharedPicker anchorText={'Projects are unavailable'} leadingIcon={AlertIcon} readonly />}
          >
            <ProjectPicker
              pickerId="create-issue-projects-picker"
              readonly={!canSetProjects}
              onSave={setProjects}
              selectedProjects={projects}
              owner={repository.owner.login}
              repo={repository.name}
              anchorElement={anchorProps => (
                <DefaultProjectPickerAnchor
                  nested={false}
                  readonly={!canSetProjects}
                  anchorProps={anchorProps}
                  firstSelectedProjectTitle={firstSelectedProjectTitle}
                  {...sharedConfigProps}
                />
              )}
              {...sharedConfigProps}
            />
          </ErrorBoundary>
        )}
        {/* Rendered when created from a Memex project */}
        {optionConfig.scopedProjectTitle && (
          <SharedPicker anchorText={optionConfig.scopedProjectTitle} leadingIcon={TableIcon} readonly />
        )}
        {(canSetMilestone || milestone !== null) && !optionConfig.scopedMilestone && (
          <MilestonePicker
            repo={repo}
            owner={owner}
            readonly={!canSetMilestone}
            canCreateMilestone={canCreateMilestone}
            showNoMatchItem
            activeMilestone={milestone}
            anchorElement={(anchorProps, ref) => (
              <DefaultMilestonePickerAnchor
                nested={false}
                readonly={!canSetMilestone}
                activeMilestone={milestone}
                anchorProps={anchorProps}
                ref={ref}
                {...sharedConfigProps}
              />
            )}
            onSelectionChange={milestones => setMilestone(milestones?.[0] || null)}
            {...sharedConfigProps}
          />
        )}
        {optionConfig.scopedMilestone && (
          <SharedPicker anchorText={optionConfig.scopedMilestone} leadingIcon={MilestoneIcon} readonly />
        )}
        {canSetIssueFields &&
          unpinnedFields.map(field => (
            <IssueFieldValueSharedEditor
              key={field.field.id}
              field={field}
              onChange={onIssueFieldValueChanged}
              className={styles.animationClass}
              readonly={field.readonly}
              showVisibilityIndicator={
                showVisibilityIndicators && !!field.field.id && fieldVisibilityMap[field.field.id] === 'ALL'
              }
            />
          ))}
        {pendingSelectedField && (
          <IssueFieldValueSharedPendingEditor
            pendingField={pendingSelectedField}
            onCommit={onPendingFieldCommit}
            onClose={() => {
              setPendingSelectedField(null)
            }}
          />
        )}
      </div>
    </Suspense>
  )
}
